import ganttLarge from 'assets/hyperbench/hyperbench-gantt-large.jpg';
import ganttPlaceholder from 'assets/hyperbench/hyperbench-gantt-placeholder.jpg';
import gantt from 'assets/hyperbench/hyperbench-gantt.jpg';
import HyperBenchLogo from 'assets/hyperbench/hyperbench-logo.svg';
import { Button } from 'components/Button';
import { Footer } from 'components/Footer';
import { Image } from 'components/Image';
import { Meta } from 'components/Meta';
import {
  ProjectContainer,
  ProjectHeader,
  ProjectSection,
  ProjectSectionContent,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectTextRow,
} from 'layouts/Project';
import { Fragment } from 'react';
import { media } from 'utils/style';
import styles from './hyperbench.module.css';

const title = 'HyperBench';
const description =
  'A benchmarking harness for job-shop scheduling. HyperBench runs exact solvers, metaheuristics and evolved dispatching rules on the same benchmark instances across a SLURM cluster, then compares the schedules they produce.';
const roles = ['Honours Project', 'Python + SLURM', 'Job-shop scheduling'];

const runStages = [
  { title: 'Instances', detail: 'ABZ, FT and LA benchmark sets' },
  { title: 'Experiments', detail: 'Solver parameters and 30 seeds per combination' },
  { title: 'Execution', detail: 'SLURM array jobs' },
  { title: 'Schedules', detail: 'Distinct results, Gantt charts and GIFs' },
];

const solverGroups = [
  { title: 'Exact and simple baselines', methods: 'OR-Tools constraint programming; priority dispatching rules' },
  { title: 'Search methods', methods: 'Genetic algorithm; simulated annealing; tabu search' },
  { title: 'Evolved rules', methods: 'Genetic programming based on the Java GPJSS code by Yi Mei and Fangfang Zhang' },
];

export function HyperBench() {
  return (
    <Fragment>
      <Meta title={title} prefix="Projects" description={description} />
      <ProjectContainer className={styles.page}>
        <ProjectHeader
          title={title}
          logo={<HyperBenchLogo aria-hidden="true" />}
          description={description}
          linkLabel="Source on GitHub"
          url="https://github.com/txmxthy/HyperBench"
          roles={roles}
        />
        <ProjectSection padding="top">
          <ProjectSectionContent>
            <figure className={styles.runFigure} aria-labelledby="hyperbench-run-title">
              <figcaption id="hyperbench-run-title" className={styles.figureTitle}>
                A comparable run from input to schedule
              </figcaption>
              <ol className={styles.runList}>
                {runStages.map(({ title: stage, detail }) => (
                  <li className={styles.runStage} key={stage}>
                    <span className={styles.stageTitle}>{stage}</span>
                    <span className={styles.stageDetail}>{detail}</span>
                  </li>
                ))}
              </ol>
            </figure>
            <ProjectTextRow>
              <ProjectSectionHeading>The comparison problem</ProjectSectionHeading>
              <ProjectSectionText>
                Job-shop scheduling orders jobs across machines to finish the last one as
                early as possible. It is NP-hard. I built HyperBench to run different
                methods on the same instances, vary their parameters, and inspect how the
                resulting schedules differ.
              </ProjectSectionText>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <div className={styles.methodsLayout}>
              <ProjectTextRow width="s">
                <ProjectSectionHeading>Methods in the harness</ProjectSectionHeading>
                <ProjectSectionText>
                  The bundled methods cover exact solving, dispatching, metaheuristics and
                  evolved dispatching rules. A new solver can use the bundled simulation
                  environment or bring its own. The included external algorithms retain
                  credit to their original authors.
                </ProjectSectionText>
              </ProjectTextRow>
              <div className={styles.methodList}>
                {solverGroups.map(({ title: group, methods }) => (
                  <div className={styles.methodGroup} key={group}>
                    <h3>{group}</h3>
                    <p>{methods}</p>
                  </div>
                ))}
              </div>
            </div>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <Image
              raised
              className={styles.gantt}
              srcSet={[gantt, ganttLarge]}
              placeholder={ganttPlaceholder}
              alt="A Gantt chart from HyperBench: OR-Tools constraint programming on the abz5 instance, ten machines by ten jobs, each job a colour across the machines it visits, finishing at 1,234."
              sizes={`(max-width: ${media.mobile}px) 100vw, 1000px`}
            />
            <ProjectTextRow>
              <ProjectSectionHeading>From a grid to a schedule</ProjectSectionHeading>
              <ProjectSectionText>
                A generator writes the parameter combinations and 30 seeds for each one
                across the ABZ, FT and LA instances. It prints a worst-case runtime before
                submission. SLURM array jobs run the experiments; post-processing selects
                distinct schedules and renders Gantt charts and GIFs.
              </ProjectSectionText>
              <ProjectSectionText>
                This was completed in partial fulfilment of a Bachelor of Engineering with
                Honours at Victoria University of Wellington, supervised by Dr Yi Mei and
                Dr Fangfang Zhang.
              </ProjectSectionText>
              <Button secondary iconHoverShift icon="chevronRight" href="https://github.com/txmxthy/HyperBench">
                Read the source
              </Button>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
      </ProjectContainer>
      <Footer />
    </Fragment>
  );
}
